import { Link } from "react-router-dom";
import { Wrench, Hammer, Flame, Droplets, Pipette, Construction, ArrowRight } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

const PIPE_IMG =
    "https://images.unsplash.com/photo-1738918897772-0ba101be25c8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwcGx1bWJpbmclMjBwaXBlc3xlbnwwfHx8fDE3Nzc5MzA4OTh8MA&ixlib=rb-4.1.0&q=85";
const REPAIR_IMG =
    "https://images.unsplash.com/photo-1676210134190-3f2c0d5cf58d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwzfHxwbHVtYmVyJTIwd29ya2luZyUyMGNvbnN0cnVjdGlvbnxlbnwwfHx8fDE3Nzc5MzA4OTh8MA&ixlib=rb-4.1.0&q=85";

const cardIcons = [Wrench, Pipette, Hammer, Flame, Droplets, Construction];
const cardCodes = ["S-01", "S-02", "S-03", "S-04", "S-05", "S-06"];

export default function Services() {
    useDocumentMeta("services", "/services");
    const { t } = useI18n();
    const s = t.services;

    return (
        <div data-testid="services-page" className="bg-white">
            <section className="bg-zinc-100 border-b-2 border-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                        <div className="lg:col-span-8">
                            <div className="overline mb-5">{s.headerOverline}</div>
                            <h1 className="font-display text-6xl md:text-7xl lg:text-8xl uppercase leading-[0.85] text-black">
                                {s.headerTitle1}
                                <br />
                                {s.headerTitle2}
                                <br />
                                <span className="text-[#FF5A00]">{s.headerTitle3}</span>
                            </h1>
                        </div>
                        <p className="lg:col-span-4 text-lg font-medium text-zinc-700 leading-relaxed">
                            {s.headerDesc}
                        </p>
                    </div>
                </div>
            </section>

            <section className="border-b-2 border-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-2 border-black bg-white">
                        {s.cards.map((c, idx) => {
                            const Icon = cardIcons[idx];
                            return (
                                <div
                                    key={c.title}
                                    data-testid={`service-card-${idx}`}
                                    className={`p-8 md:p-10 group hover:bg-black hover:text-white transition-colors duration-300 ${
                                        idx % 3 !== 2 ? "lg:border-r-2" : ""
                                    } ${idx % 2 === 0 ? "md:border-r-2 lg:border-r-2" : "md:border-r-0"} border-b-2 last:border-b-0 border-black`}
                                >
                                    <div className="flex items-start justify-between mb-10">
                                        <div className="w-14 h-14 border-2 border-current flex items-center justify-center">
                                            <Icon className="w-7 h-7 group-hover:text-[#FF5A00]" strokeWidth={2.5} />
                                        </div>
                                        <div className="font-display text-2xl tracking-wider opacity-50">
                                            {cardCodes[idx]}
                                        </div>
                                    </div>
                                    <h3 className="font-display text-3xl md:text-4xl uppercase leading-none">
                                        {c.title}
                                    </h3>
                                    <p className="mt-4 font-medium opacity-80 leading-relaxed">{c.desc}</p>
                                    <ul className="mt-6 space-y-1.5">
                                        {c.items.map((it) => (
                                            <li
                                                key={it}
                                                className="text-xs uppercase tracking-[0.18em] font-bold flex items-center gap-2"
                                            >
                                                <span className="text-[#FF5A00]">→</span>
                                                {it}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="border-b-2 border-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="relative">
                            <img src={PIPE_IMG} alt="Industrial plumbing pipes" className="w-full h-[380px] object-cover border-2 border-black grayscale" />
                            <div className="absolute top-5 left-5 bg-[#FF5A00] text-black px-4 py-2 border-2 border-black text-xs uppercase tracking-[0.3em] font-bold">
                                {s.tagIndustrial}
                            </div>
                        </div>
                        <div className="relative">
                            <img src={REPAIR_IMG} alt="Plumber repairing water heater" className="w-full h-[380px] object-cover border-2 border-black grayscale" />
                            <div className="absolute top-5 left-5 bg-black text-white px-4 py-2 border-2 border-black text-xs uppercase tracking-[0.3em] font-bold">
                                {s.tagRepair}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="bg-black text-white border-b-2 border-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
                    <div className="overline mb-4">{s.processOverline}</div>
                    <h2 className="font-display text-5xl md:text-6xl uppercase leading-none">
                        {s.processTitle1} <span className="text-[#FF5A00]">{s.processTitle2}</span>
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-4 mt-14 border-2 border-white">
                        {s.process.map(([n, ti, d], i) => (
                            <div
                                key={n}
                                className={`p-8 ${i !== 3 ? "md:border-r-2 border-white" : ""} border-b-2 md:border-b-0 last:border-b-0`}
                            >
                                <div className="font-display text-5xl text-[#FF5A00] leading-none">{n}</div>
                                <div className="font-display text-2xl uppercase mt-3">{ti}</div>
                                <p className="mt-3 font-medium text-zinc-300">{d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="bg-[#FF5A00] border-b-2 border-black">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
                    <h2 className="font-display text-5xl md:text-6xl uppercase leading-none text-black">
                        {s.ctaTitle}
                    </h2>
                    <Link to="/contact" data-testid="services-cta" className="brutal-btn">
                        {s.ctaBtn} <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
